/**
 * @description Connect Domain flow for the Lynx Website tab (LYN-20, LYN-146, LYN-427)
 *
 * Company sites register www.{domain} and show one CNAME whose host label is www.
 * Sales Org sites keep the apex input and apex + www registration.
 * Unpaid: Connect Domain visible but disabled with administrator contact footnote.
 */

import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Globe,
  Link2,
  Loader2,
  RefreshCw
} from 'lucide-react';

const POLL_INTERVAL_MS = 30000;

const STEPS = {
  UPGRADE: 'upgrade',
  DOMAIN: 'domain',
  DNS: 'dns',
  CONNECTED: 'connected'
};

/**
 * Company connect always stores one www label. www.www.example.com becomes www.example.com.
 *
 * @param {string} raw
 * @returns {string}
 */
const normalizeCompanyWwwInput = (raw) => {
  let host = String(raw || '').trim().toLowerCase().replace(/\.$/, '');
  host = host.replace(/^https?:\/\//, '').split('/')[0];
  while (host.startsWith('www.')) {
    host = host.slice('www.'.length);
  }
  if (!host) {
    return '';
  }
  return `www.${host}`;
};

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
      aria-label={label}
    >
      {copied ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy
        </>
      )}
    </button>
  );
};

const ApexForwardModal = ({ apex, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="presentation">
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="apex-forward-title"
      className="w-full max-w-lg rounded-xl bg-white shadow-xl p-5 space-y-4"
    >
      <div>
        <h3 id="apex-forward-title" className="text-base font-semibold text-gray-900">
          Forward {apex} to www
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Use a permanent 301 to https://www.{apex}. Do not use masking or frame forwarding.
        </p>
      </div>
      <div className="space-y-3 text-sm text-gray-800">
        <div>
          <p className="font-medium text-gray-900">GoDaddy</p>
          <p>Domain → Forwarding → Add. Forward {apex} to https://www.{apex}. Permanent (301).</p>
        </div>
        <div>
          <p className="font-medium text-gray-900">Namecheap</p>
          <p>Domain List → Manage → Redirect Domain. Destination https://www.{apex}. Permanent (301). Unmasked.</p>
        </div>
        <div>
          <p className="font-medium text-gray-900">Cloudflare</p>
          <p>Rules → Redirect Rules. If hostname equals {apex}, redirect to https://www.{apex} with status 301.</p>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const DnsRecordRow = ({ record }) => {
  const hostLabel = record.displayHost || record.hostname;

  return (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {record.type} record
      </span>
      <span className="text-xs text-gray-500">{record.label}</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1">Host / Name</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-gray-900 break-all">{hostLabel}</code>
          <CopyButton value={hostLabel} label="Copy hostname" />
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1">Points to / Value</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-gray-900 break-all">{record.value}</code>
          <CopyButton value={record.value} label="Copy target" />
        </div>
      </div>
    </div>
  </div>
  );
};

/**
 * @param {object} props
 * @param {string} props.defaultHostname - Lynx subdomain hostname
 * @param {object|null} props.domainStatus - API payload from GET /cms-site/domain
 * @param {boolean} props.loading
 * @param {string|null} props.error
 * @param {string|null} props.connectError
 * @param {boolean} props.connecting
 * @param {string|null} props.disconnectError
 * @param {boolean} props.disconnecting
 * @param {() => Promise<void>} props.onRefresh
 * @param {(domain: string) => Promise<boolean>} props.onConnect
 * @param {() => Promise<boolean>} props.onDisconnect
 */
const ConnectDomainPanel = ({
  defaultHostname,
  domainStatus,
  loading,
  error,
  connectError,
  connecting,
  disconnectError,
  disconnecting,
  onRefresh,
  onConnect,
  onDisconnect
}) => {
  const [step, setStep] = useState(STEPS.UPGRADE);
  const [domainInput, setDomainInput] = useState('');
  const [forwardOpen, setForwardOpen] = useState(false);

  const customDomain = domainStatus?.customDomain;
  const billingGate = domainStatus?.billingGate;
  const isCompany = domainStatus?.ownerType === 'company';
  const isVerified = customDomain?.status === 'verified';
  const isPending = customDomain?.status === 'pending';
  const isFailed = customDomain?.status === 'failed';
  const showWwwInstruction = isCompany
    && typeof customDomain?.hostname === 'string'
    && customDomain.hostname.startsWith('www.');
  const apexFromWww = showWwwInstruction
    ? customDomain.hostname.replace(/^www\./, '')
    : '';

  useEffect(() => {
    if (isVerified) {
      setStep(STEPS.CONNECTED);
      return;
    }
    if (isPending || isFailed) {
      setStep(STEPS.DNS);
      if (customDomain?.hostname && !domainInput) {
        setDomainInput(customDomain.hostname);
      }
      return;
    }
    if (billingGate?.required) {
      setStep(STEPS.UPGRADE);
      return;
    }
    setStep(STEPS.DOMAIN);
  }, [customDomain?.hostname, customDomain?.status, billingGate?.required, domainInput, isFailed, isPending, isVerified]);

  useEffect(() => {
    if (!isCompany || isVerified || isPending || isFailed || billingGate?.required) {
      return;
    }
    setDomainInput((current) => (current === '' ? 'www.' : current));
  }, [billingGate?.required, isCompany, isFailed, isPending, isVerified]);

  useEffect(() => {
    if (!isPending) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      onRefresh();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isPending, onRefresh]);

  const handleConnectSubmit = async (event) => {
    event.preventDefault();
    const trimmed = domainInput.trim();
    const domain = isCompany ? normalizeCompanyWwwInput(trimmed) : trimmed;
    if (!domain) {
      return;
    }
    const ok = await onConnect(domain);
    if (ok) {
      setStep(STEPS.DNS);
    }
  };

  const handleDisconnect = async () => {
    const ok = await onDisconnect();
    if (ok) {
      setDomainInput(isCompany ? 'www.' : '');
      setForwardOpen(false);
      setStep(STEPS.DOMAIN);
    }
  };

  if (loading && !domainStatus) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-red-800">{error}</p>
          <button
            type="button"
            onClick={onRefresh}
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-red-900 hover:underline"
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-indigo-50 p-2.5 flex-shrink-0">
          <Globe className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">Connect Domain</h3>
          <p className="text-sm text-gray-600 mt-1">
            Use your own domain for your public site. Your site is currently available at{' '}
            <span className="font-mono text-gray-800">{defaultHostname}</span>.
          </p>
        </div>
      </div>

      {step === STEPS.CONNECTED && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-5 space-y-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
            <div>
              <h4 className="text-base font-semibold text-green-900">Domain Connected</h4>
              <p className="text-sm text-green-800 mt-1">
                <span className="font-mono">{customDomain?.hostname}</span> is verified and serving
                your site.
              </p>
            </div>
          </div>
          {disconnectError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{disconnectError}</span>
            </div>
          )}
          <button
            type="button"
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-800 bg-white rounded-lg hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {disconnecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Disconnecting…
              </>
            ) : (
              'Disconnect domain'
            )}
          </button>
        </div>
      )}

      {step === STEPS.UPGRADE && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Custom domains require a Paid hosting plan</p>
            <p className="text-sm text-gray-600 mt-1">
              Upgrade to a paid website plan to connect your own domain to your public site.
            </p>
          </div>
          <button
            type="button"
            disabled
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-200 text-gray-500 rounded-lg text-sm font-medium cursor-not-allowed"
          >
            <Link2 className="h-4 w-4" />
            Connect domain
          </button>
          <p className="text-xs text-gray-500">
            Contact your administrator to upgrade your hosting plan.
          </p>
        </div>
      )}

      {step === STEPS.DOMAIN && (
        <form onSubmit={handleConnectSubmit} className="space-y-4">
          <div>
            <label htmlFor="cms-custom-domain" className="block text-sm font-medium text-gray-700">
              {isCompany ? 'Your domain' : 'Your domain (apex)'}
            </label>
            <p className="text-xs text-gray-500 mt-1">
              {isCompany ? (
                <>
                  Enter your domain, for example <span className="font-mono">yourbrand.com</span>.
                  The site address is www plus that domain.
                </>
              ) : (
                <>
                  Enter your root domain, for example <span className="font-mono">yourbrand.com</span>.
                  {' '}
                  {customDomain?.dnsInstructions?.wwwNote
                    || 'www will work automatically — redirecting www to your apex domain is supported.'}
                </>
              )}
            </p>
            <input
              id="cms-custom-domain"
              type="text"
              value={domainInput}
              onChange={(event) => setDomainInput(event.target.value)}
              placeholder="yourbrand.com"
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-mono shadow-sm focus:border-brand focus:ring-brand"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {connectError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{connectError}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={connecting || !domainInput.trim()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {connecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Registering domain…
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4" />
                Connect domain
              </>
            )}
          </button>
        </form>
      )}

      {step === STEPS.DNS && customDomain?.dnsInstructions && (
        <div className="space-y-4">
          {isPending && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 flex items-start gap-3">
              <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Waiting for DNS</p>
                <p className="text-sm text-blue-800 mt-1">
                  Add the record below at your domain registrar. We check automatically — no
                  refresh needed.
                </p>
              </div>
            </div>
          )}

          {isFailed && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Verification failed</p>
                <p className="text-sm text-red-800 mt-1">
                  Confirm the CNAME record matches exactly, then try connecting again.
                </p>
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-gray-900 mb-2">DNS records</p>
            <p className="text-xs text-gray-500 mb-3">
              {showWwwInstruction ? (
                <>
                  Add this CNAME at your domain registrar. The value is{' '}
                  <span className="font-mono">{customDomain.dnsInstructions.target}</span>.
                </>
              ) : (
                <>
                  Point your apex domain using a CNAME (or equivalent ALIAS/ANAME at your
                  registrar) targeting{' '}
                  <span className="font-mono">{customDomain.dnsInstructions.target}</span>.
                </>
              )}
            </p>
            <div className="space-y-3">
              {customDomain.dnsInstructions.records.map((record) => (
                <DnsRecordRow key={`${record.type}-${record.hostname}`} record={record} />
              ))}
            </div>
            {customDomain.dnsInstructions.wwwNote && !showWwwInstruction && (
              <p className="text-xs text-gray-500 mt-3">{customDomain.dnsInstructions.wwwNote}</p>
            )}
            {showWwwInstruction && (
              <button
                type="button"
                onClick={() => setForwardOpen(true)}
                className="mt-3 text-sm text-brand hover:underline"
              >
                Want {apexFromWww} (without www) to also work?
              </button>
            )}
          </div>

          {disconnectError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{disconnectError}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {disconnecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Disconnecting…
              </>
            ) : (
              'Cancel / disconnect domain'
            )}
          </button>
        </div>
      )}
      {forwardOpen && showWwwInstruction && (
        <ApexForwardModal apex={apexFromWww} onClose={() => setForwardOpen(false)} />
      )}
    </div>
  );
};

export default ConnectDomainPanel;
